import type { Nullable } from '@ats/interfaces';
import { useInterviewAPI } from '@hooks/interview/useInterviewAPI';
import { useInterviewSession } from '@hooks/interview/useInterviewSession';
import type { RosterAttendeeType, RosterType } from '@interfaces/chime';
import { DefaultModality } from 'amazon-chime-sdk-js';
import type { FC, PropsWithChildren } from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AttendeeInfoDTO } from '../../../../server/model/meeting.room.dto';
import { useAudioVideo } from '../AudioVideoProvider';
import { useMeetingManager } from '../MeetingProvider';

interface RosterContextValue {
  roster: RosterType;
  attendees: AttendeeInfoDTO[];
}

const RosterContext = createContext<Nullable<RosterContextValue>>(null);

export const RosterProvider: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { getInterviewInfo } = useInterviewSession();
  const { certificationCode } = getInterviewInfo();
  const { fetchAttendeesDetail } = useInterviewAPI();
  const meetingManager = useMeetingManager();
  const audioVideo = useAudioVideo();
  const rosterRef = useRef<RosterType>({});
  const [roster, setRoster] = useState<RosterType>({});
  const [attendees, setAttendees] = useState<AttendeeInfoDTO[]>([]);

  useEffect(() => {
    if (!audioVideo) {
      return;
    }

    const rosterUpdateCallback = async (
      chimeAttendeeId: string,
      present: boolean,
      externalUserId?: string,
    ): Promise<void> => {
      const { data } = await fetchAttendeesDetail({ certificationCode });
      if ('attendees' in data) {
        setAttendees(data.attendees);
      }

      if (!present) {
        delete rosterRef.current[chimeAttendeeId];

        setRoster((currentRoster: RosterType) => {
          const { [chimeAttendeeId]: _, ...rest } = currentRoster;
          return { ...rest };
        });

        return;
      }

      const attendeeId = new DefaultModality(chimeAttendeeId).base();
      if (attendeeId !== chimeAttendeeId) {
        return;
      }

      const inRoster = rosterRef.current[chimeAttendeeId];
      if (inRoster) {
        return;
      }

      let attendee: RosterAttendeeType = { chimeAttendeeId };

      if (externalUserId) {
        attendee.externalUserId = externalUserId;
      }

      rosterRef.current[attendeeId] = attendee;

      // Update the roster first before waiting to fetch attendee info
      setRoster((oldRoster) => ({
        ...oldRoster,
        [attendeeId]: attendee,
      }));

      if (meetingManager.getAttendee) {
        const externalData = await meetingManager.getAttendee(
          attendeeId,
          externalUserId,
        );

        // Make sure that the attendee is still on the roster
        if (!rosterRef.current[attendeeId]) {
          return;
        }
        attendee = { ...attendee, ...externalData };
        setRoster((oldRoster) => ({
          ...oldRoster,
          [attendeeId]: attendee,
        }));
      }
    };

    audioVideo.realtimeSubscribeToAttendeeIdPresence(rosterUpdateCallback);

    return () => {
      setRoster({});
      setAttendees([]);
      rosterRef.current = {};
      audioVideo.realtimeUnsubscribeToAttendeeIdPresence(rosterUpdateCallback);
    };
  }, [audioVideo]);

  const value = useMemo(
    () => ({
      roster,
      attendees,
    }),
    [roster],
  );

  return (
    <RosterContext.Provider value={value}>{children}</RosterContext.Provider>
  );
};

export function useRosterState(): RosterContextValue {
  const state = useContext(RosterContext);

  if (!state) {
    throw new Error('userRosterState must be used within RosterProvider');
  }

  return state;
}
